import type { RemoveFormula } from "building-types/options/formula";
import type { Roof, RoofDormerPlacement } from "building-types/roofs/roofSystemTypes";
import type { DormerSettings } from "building-types/settings/dormerSettings";
import type { PartialHomeSettings } from "building-types/settings/homeSettings";
import { type MergeSettingsForEntityParams } from "building-types/settings/mergeSettingsForEntityParams";
import type { RoofSettings } from "building-types/settings/roofSettings";
import { mergeSettingsForEntity } from "doc/mergeSettingsSet";

/**
 * The order in which these settings are merged is as follows:
 * 1. home settings
 * 2. targeted settings
 * 3. roof instance
 */

export type MergedRoofSettings = RemoveFormula<RoofSettings>;

interface FinalRoofSettingsParams extends Omit<MergeSettingsForEntityParams, "homeSettings"> {
  readonly roof: Roof;
  readonly homeSettings: RemoveFormula<RoofSettings>;
}

export default function getMergedRoofSettings({
  roof,
  settings,
  homeSettings,
  optionChoices,
  entitySettings,
  activeSettingsSets,
}: FinalRoofSettingsParams): RemoveFormula<RoofSettings> {
  const mergedTargetedSettings: PartialHomeSettings = mergeSettingsForEntity(
    entitySettings,
    roof.id,
    activeSettingsSets,
    settings,
    optionChoices,
  );

  let filteredInstanceSettings: Partial<RemoveFormula<RoofSettings>> = {};
  for (const id in roof.roofSettings) {
    const val = roof.roofSettings[id];

    if (val !== undefined && val !== null) {
      filteredInstanceSettings[id] = val;
    }
  }

  if (filteredInstanceSettings.slope !== undefined && filteredInstanceSettings.rightSlope === undefined) {
    filteredInstanceSettings = {
      ...filteredInstanceSettings,
      rightSlope: filteredInstanceSettings.slope,
    };
  }

  if (filteredInstanceSettings.slopes !== undefined && filteredInstanceSettings.rightSlopes === undefined) {
    filteredInstanceSettings = {
      ...filteredInstanceSettings,
      rightSlopes: filteredInstanceSettings.slopes,
    };
  }

  return {
    ...homeSettings,
    ...(mergedTargetedSettings.roof as RemoveFormula<RoofSettings>),
    ...filteredInstanceSettings,
  };
}

interface FinalDormerSettingsParams extends MergeSettingsForEntityParams {
  dormerSettings: DormerSettings;
  dormer: RoofDormerPlacement;
}

export function getMergedDormerSettings({
  dormerSettings,
  dormer,
  settings,
  optionChoices,
  entitySettings,
  activeSettingsSets,
}: FinalDormerSettingsParams): DormerSettings {
  const mergedTargetedSettings: PartialHomeSettings = mergeSettingsForEntity(
    entitySettings,
    dormer.id,
    activeSettingsSets,
    settings,
    optionChoices,
  );

  const filteredInstanceSettings: Partial<DormerSettings> = {};
  for (const id in dormer.dormerSettings) {
    const val = dormer.dormerSettings[id];

    if (val !== undefined && val !== null) {
      filteredInstanceSettings[id] = val;
    }
  }

  return {
    ...dormerSettings,
    ...mergedTargetedSettings.dormer,
    ...filteredInstanceSettings,
  };
}
